/**
  * This program is free software; you can redistribute it and/or
  * modify it under the terms of the GNU General Public License
  * as published by the Free Software Foundation; under version 2
  * of the License (non-upgradable).
  *
  * This program is distributed in the hope that it will be useful,
  * but WITHOUT ANY WARRANTY; without even the implied warranty of
  * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  * GNU General Public License for more details.
  *
  * You should have received a copy of the GNU General Public License
  * along with this program; if not, write to the Free Software
  * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA 02110-1301, USA.
  *
  * Copyright (c) 2022-2024 (original work) Open Assessment Technologies SA;
  */
import { derived, writable, get } from 'svelte/store';
import configurationStore from './configuration';
import { sessionStorage } from '../core/utils/storage';

function createSchoolSettingsStore() {
    const schoolSettingsFields = derived([configurationStore], ([$configurationStore]) => $configurationStore.fields.additionalData.reduce((acc, schoolSetting) => {
            const fieldName = schoolSetting.name;
            acc[fieldName] = schoolSetting;
            return acc;
        }, {}));
    const schoolSettingsValues = writable({});

    const { subscribe } = derived(
        [schoolSettingsFields, schoolSettingsValues],
        ([$schoolSettingsFields, $schoolSettingsValues]) => ({
            fieldsConfiguration: $schoolSettingsFields,
            values: $schoolSettingsValues,
            areRequiredSettingsFilled:
                Object.values($schoolSettingsFields).every((fieldConfig) => !fieldConfig.required || !!$schoolSettingsValues[fieldConfig.name])
        })
    );

    return {
        subscribe,
        /**
         * Update value in schoolSettingsValues store
         * @param {string} key 
         * @param {any} newValue 
         */
        async updateSchoolSettingsValue(key, newValue) {
            schoolSettingsValues.update(s => ({
                ...s,
                [key]: newValue
            }));
            await this.storeValues();
        },
        async setValues(newValues) {
            schoolSettingsValues.set(newValues);
            await this.storeValues();
        },
        async storeValues() {
            const storage = await sessionStorage;
            storage.setItem('schoolSettings', JSON.stringify(get(schoolSettingsValues)));
        },
        async restoreValues() {
            const storage = await sessionStorage;

            let values = {};
            try {
                const unparsedValues = await storage.getItem('schoolSettings');
                values = JSON.parse(unparsedValues);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Couldn\'t parse school settings values from session storage');
            } finally {
                schoolSettingsValues.set(values);
            }
        }
    }
}

export default createSchoolSettingsStore();